import { jwtDecode } from 'jwt-decode';
import { fromUnixTime, isPast } from 'date-fns';
import { fetchAuthSession } from 'aws-amplify/auth';
import cookies from 'js-cookie';
import {
  ADMIN_GROUP_TYPE,
  COGNITO_IDENTITY_FIELDS,
  CUSTOMER_GROUP_TYPE,
  GOOGLE_SSO_USERNAME_PREFIX,
  SUPPLIER_GROUP_TYPE,
} from '../../constants-togather/cognito';
import {
  AUTH_TOKEN,
  COGNITO_SSO_LOGOUT,
  IS_IMPERSONATION_SESSION,
  USER_ID,
} from '../../constants-togather/auth';
import { storeToken } from '../../modules/token-storage';

type DecodedCognitoUserToken = {
  exp: number;
  iat: number;
  iss: string;
  jti: string;
  origin_jti: string;
  scope: string;
  client_id: string;
  auth_time: number;
  'cognito:groups': string[];
  sub: string;
  token_use: string;
  version: number;
  username: string;
};

export const getImpersonatingUserUuid = (): string | undefined =>
  cookies.get(IS_IMPERSONATION_SESSION);

export const isCognitoAuthentication = (token: string): boolean => {
  if (!token) {
    return false;
  }

  try {
    const decoded = jwtDecode<DecodedCognitoUserToken>(token);

    return Array.isArray(decoded[COGNITO_IDENTITY_FIELDS.GROUPS]);
  } catch {
    return false;
  }
};

export const isGoogleSSOUser = (): boolean => {
  const authenticationToken = cookies.get(AUTH_TOKEN);

  if (!authenticationToken) {
    return false;
  }
  const decoded = jwtDecode<DecodedCognitoUserToken>(authenticationToken);

  return (
    Array.isArray(decoded[COGNITO_IDENTITY_FIELDS.GROUPS]) &&
    !!decoded[COGNITO_IDENTITY_FIELDS.USERNAME]?.startsWith(
      GOOGLE_SSO_USERNAME_PREFIX
    )
  );
};

export const isImpersonationSession = (): boolean => {
  const authenticationToken = cookies.get(AUTH_TOKEN);

  if (!authenticationToken) {
    return false;
  }

  if (isCognitoAuthentication(authenticationToken)) {
    return Boolean(getImpersonatingUserUuid());
  }

  const { impersonationSession }: { impersonationSession: boolean } = jwtDecode(
    authenticationToken
  );

  return impersonationSession ?? false;
};

export const isNewlyCreatedUser = (
  idToken: Record<string, unknown>
): boolean => {
  if (!idToken) {
    return false;
  }

  return Boolean(!idToken[COGNITO_IDENTITY_FIELDS.DATABASE_ID]);
};

export const getLatestCognitoToken = async (
  forceRefresh?: boolean
): Promise<string | undefined> => {
  if (isImpersonationSession()) {

    return cookies.get(AUTH_TOKEN);
  }

  let session = await fetchAuthSession({ forceRefresh });

  if (!session?.tokens?.accessToken) {
    return undefined;
  }

  if (isPast(fromUnixTime(session.tokens.accessToken.payload.exp))) {
    session = await fetchAuthSession({
      forceRefresh,
    });
  }

  if (session.tokens?.accessToken) {
    // Keep the access token in the AUTH_TOKEN cookie: it is decoded elsewhere
    // (isCognitoAuthentication, isGoogleSSOUser, expiry checks).
    storeToken(session.tokens?.accessToken?.toString());
  }
  // Send the id token as the bearer: it carries `custom:databaseId`, which the
  // backends read to identify the user (the access token does not).
  return session.tokens?.idToken?.toString();
};

// Returns the Cognito ACCESS token. Used only by the realtime socket, whose
// backend has not been migrated to read identity from the id token. HTTP /
// GraphQL requests use getLatestCognitoToken (id token).
export const getLatestCognitoAccessToken = async (
  forceRefresh?: boolean
): Promise<string | undefined> => {
  if (isImpersonationSession()) {
    return cookies.get(AUTH_TOKEN);
  }

  let session = await fetchAuthSession({ forceRefresh });

  if (!session?.tokens?.accessToken) {
    return undefined;
  }

  if (isPast(fromUnixTime(session.tokens.accessToken.payload.exp))) {
    session = await fetchAuthSession({ forceRefresh });
  }

  return session.tokens?.accessToken?.toString();
};

// The current user's database id, read from the verified id token claim
// (`custom:databaseId`). Replaces the USER_ID cookie as the source of identity.
export const getCurrentDatabaseId = async (): Promise<string | undefined> => {
  // During impersonation there is no Amplify session — the identity is the
  // admin-selected target carried in the USER_ID cookie (mirrors
  // getLatestCognitoToken). Without this the realtime socket sends an empty
  // `x-user-id`, the messaging backend replies with an error object, and that
  // object ends up rendered in the header (React "objects are not valid as a
  // child" crash → white screen).
  if (isImpersonationSession()) {
    return cookies.get(USER_ID);
  }

  const session = await fetchAuthSession();
  const databaseId =
    session.tokens?.idToken?.payload?.[COGNITO_IDENTITY_FIELDS.DATABASE_ID];

  return typeof databaseId === 'string' ? databaseId : undefined;
};

export const isCognitoUserCustomer = (token) => {
  if (!token) {
    return false;
  }

  const decoded = jwtDecode<DecodedCognitoUserToken>(token);

  return (
    decoded[COGNITO_IDENTITY_FIELDS.GROUPS]?.includes(CUSTOMER_GROUP_TYPE) ||
    decoded[COGNITO_IDENTITY_FIELDS.GROUPS]?.includes(ADMIN_GROUP_TYPE)
  );
};

export const isCognitoUserSupplier = (token) => {
  if (!token) {
    return false;
  }
  const decoded = jwtDecode<DecodedCognitoUserToken>(token);

  return decoded[COGNITO_IDENTITY_FIELDS.GROUPS]?.includes(SUPPLIER_GROUP_TYPE);
};

export const removeSSOLogoutFlag = (): void => {
  localStorage.removeItem(COGNITO_SSO_LOGOUT);
};

export const isSSOLogoutFlagSet = (): boolean => {
  return !!localStorage.getItem(COGNITO_SSO_LOGOUT);
};
